import { getFlowMeta } from './flowMeta';
import { navEntries } from './navigationConfig';

export const NAVIGATION_RECENTS_STORAGE_KEY = 'bes-ui-navigation-recents';
export const NAVIGATION_FAVORITES_STORAGE_KEY = 'bes-ui-navigation-favorites';
export const MAX_RECENT_PAGES = 6;
const DEFAULT_NAV_KEY = '/dashboard';

const GLOBAL_COMMAND_ITEMS = [
  {
    id: 'command:new-invoice',
    label: 'Create Invoice',
    hint: 'Start a new billing document.',
    path: '/invoices/new',
    category: 'Create',
    keywords: ['new', 'invoice', 'create', 'bill'],
  },
  {
    id: 'command:new-quotation',
    label: 'Create Quotation',
    hint: 'Prepare a new customer proposal.',
    path: '/quotations/new',
    category: 'Create',
    keywords: ['new', 'quotation', 'quote', 'create'],
  },
  {
    id: 'command:new-estimated-invoice',
    label: 'Create Estimated Invoice',
    hint: 'Draft projected billing values.',
    path: '/estimated-invoices/new',
    category: 'Create',
    keywords: ['new', 'estimate', 'estimated invoice', 'create'],
  },
];

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function getSessionUserKey(session) {
  if (!session?.user) {
    return 'guest';
  }

  return (
    String(session.user.id || session.user._id || session.user.email || 'guest')
      .trim()
      .toLowerCase() || 'guest'
  );
}

function findTrackableNavEntry(pathname, availableEntries = navEntries) {
  return (
    availableEntries.find(
      (entry) =>
        pathname === entry.key || pathname.startsWith(`${entry.key}/`)
    ) || null
  );
}

export function buildNavigationAidsScope(session) {
  const tenantId = String(session?.tenantId || 'public').trim().toLowerCase() || 'public';
  return `${tenantId}:${getSessionUserKey(session)}`;
}

export function isTrackablePath(pathname) {
  return isNonEmptyString(pathname) && !pathname.startsWith('/keyboard');
}

export function normalizeFavoriteKeys(value, availableEntries = navEntries) {
  if (!Array.isArray(value)) {
    return [];
  }

  const allowedKeys = new Set(availableEntries.map((entry) => entry.key));
  return Array.from(new Set(value.filter((item) => isNonEmptyString(item) && allowedKeys.has(item))));
}

export function normalizeRecentPages(
  value,
  availableEntries = navEntries,
  { hasPathAccess = () => true } = {}
) {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set();

  return value
    .filter((item) => {
      return (
        item &&
        isNonEmptyString(item.path) &&
        isNonEmptyString(item.title) &&
        isNonEmptyString(item.section) &&
        isTrackablePath(item.path) &&
        hasPathAccess(item.path)
      );
    })
    .filter((item) => {
      if (seen.has(item.path)) {
        return false;
      }

      seen.add(item.path);
      return true;
    })
    .slice(0, MAX_RECENT_PAGES)
    .map((item) => ({
      path: item.path,
      title: item.title,
      section: item.section,
      hint: isNonEmptyString(item.hint) ? item.hint : '',
      navKey:
        findTrackableNavEntry(item.path, availableEntries)?.key || DEFAULT_NAV_KEY,
    }))
    .filter((item) => isNonEmptyString(item.path));
}

export function buildRecentPageRecord(
  pathname,
  availableEntries = navEntries,
  { hasPathAccess = () => true } = {}
) {
  if (!isTrackablePath(pathname) || !hasPathAccess(pathname)) {
    return null;
  }

  const flowMeta = getFlowMeta(pathname);
  const navEntry = findTrackableNavEntry(pathname, availableEntries);
  if (!navEntry) {
    return null;
  }

  return {
    path: pathname,
    title: flowMeta.title,
    section: flowMeta.section,
    hint: flowMeta.summary || navEntry.hint || '',
    navKey: navEntry.key,
  };
}

export function getBreadcrumbItems(
  pathname,
  availableEntries = navEntries,
  { hasPathAccess = () => true } = {}
) {
  if (!isTrackablePath(pathname) || !hasPathAccess(pathname)) {
    return [];
  }

  const flowMeta = getFlowMeta(pathname);
  const navEntry = findTrackableNavEntry(pathname, availableEntries);
  if (!navEntry) {
    return [];
  }

  if (pathname === navEntry.key) {
    return [
      {
        key: pathname,
        label: flowMeta.title,
        path: pathname,
      },
    ];
  }

  const items = [
    {
      key: navEntry.key,
      label: navEntry.title,
      path: navEntry.key,
    },
  ];

  if (flowMeta.title !== navEntry.title) {
    items.push({
      path: pathname,
      key: pathname,
      label: flowMeta.title,
    });
  }

  return items.filter((item) => !item.path || hasPathAccess(item.path));
}

export function getNavigationCommandItems({
  favoriteEntries,
  recentPages,
  pathname,
  availableEntries = navEntries,
}) {
  const flowMeta = getFlowMeta(pathname);
  const quickActions = (flowMeta.quickActions || [])
    .filter((action) => isNonEmptyString(action.path) && isNonEmptyString(action.label))
    .map((action) => ({
      id: `quick:${action.path}`,
      label: action.label,
      hint: flowMeta.title,
      path: action.path,
      category: 'Current page',
      keywords: [flowMeta.title, flowMeta.section, action.label],
    }));

  const pageItems = availableEntries.map((entry) => ({
    id: `page:${entry.key}`,
    label: entry.title,
    hint: entry.hint,
    path: entry.key,
    category: 'Pages',
    keywords: [entry.title, entry.hint, entry.section],
    icon: entry.icon,
  }));

  const favoriteItems = favoriteEntries.map((entry) => ({
    id: `favorite:${entry.key}`,
    label: entry.title,
    hint: entry.hint,
    path: entry.key,
    category: 'Favorites',
    keywords: [entry.title, entry.hint, 'favorite'],
    icon: entry.icon,
  }));

  const recentItems = recentPages
    .filter((item) => item.path !== pathname)
    .map((item) => ({
      id: `recent:${item.path}`,
      label: item.title,
      hint: item.hint || item.section,
      path: item.path,
      category: 'Recent',
      keywords: [item.title, item.section, item.hint || 'recent'],
      icon: availableEntries.find((entry) => entry.key === item.navKey)?.icon,
    }));

  const dedupe = new Set();

  return [...favoriteItems, ...recentItems, ...quickActions, ...GLOBAL_COMMAND_ITEMS, ...pageItems]
    .filter((item) => {
      if (!isNonEmptyString(item.path) || dedupe.has(item.path)) {
        return false;
      }

      dedupe.add(item.path);
      return true;
    });
}
